import { Component, ViewEncapsulation, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Company } from '../company-settings/shared/models/Company';
import { CompanyManagementService } from '../company-settings/shared/services/companyManagement.service';
import { UserRole, UserRoleNames } from 'src/core/helpers/user-roles';
import { SpinnerService } from 'src/core/services/spinner.service';
import { UserService } from 'src/core/services/user.service';
import { Boards } from 'src/core/helpers/boards';
import { FormBuilder, Validators } from '@angular/forms';
import { Member } from '../shared/models/member';
import { NotificationService, Notification } from 'src/app/search-assign/shared/services/notification.service';

@Component({
  selector: 'app-general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class GeneralSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  routerSubscription!: Subscription;
  paramsSubscription!: Subscription;
  private companyNameSubscription?: Subscription;
  companyData: Company = {};
  members: Member[] = [];
  boardId!: string;
  activeTab: string = 'members';
  sliderStyle: any = { width: '196px', left: '8px' };
  isAdmin: boolean = false;
  currentUser = JSON.parse(localStorage.getItem('currentUser')!);
  currentUserRole = {
    id: this.currentUser.board.roles[0],
  };
  isInviteModalOpen = false;
  roles: any[] = [];
  inviteForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    userRole: ['', Validators.required],
  });
  activeNotification: Notification | null = null;
  notificationTimer: any;
  isCurrentBoardSameAsTargetedBoard: boolean = false;
  currentRigtsholderId: any;
  currentRigtsholderName: any;
  constructor(
    private companyService: CompanyManagementService,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private router: Router,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void {
    this.boardId = JSON.parse(localStorage.getItem('currentUser')!).board.id;
    this.isAdmin = UserRole.Superadmin == this.currentUserRole.id;

    // Re-initialize whenever the route :id param changes (same component reused by Angular router)
    this.paramsSubscription = this.route.params.subscribe((params: Params) => {
      const targetBoardId = params['id'];
      const currentBoardId = this.currentUser?.board?.id;
      this.isCurrentBoardSameAsTargetedBoard = currentBoardId === targetBoardId;

      this.currentRigtsholderName = window.history.state?.labelName || '';
      if (!this.currentRigtsholderName) {
        this.fetchCurrentRightsholderName(targetBoardId);
      }

      this.getRoles();

      // Force child tab components (e.g. app-members) to fully destroy and recreate
      // so they re-fetch data for the new boardId. Without this, if activeTab is already
      // 'members', the *ngIf never toggles and ngOnInit of app-members never re-runs.
      this.activeTab = '';
      setTimeout(() => this.setActiveTab('members'), 0);
    });

    this.routerSubscription = this.route.queryParams.subscribe((params: Params) => {
      const tab = params['tab'];
      if (tab) {
        this.activeTab = tab;
        this.updateSliderPosition(tab);
      }
    });

    this.notificationService.notification$.subscribe(notification => {
      this.showNotification(notification);
    });
  }

  private fetchCurrentRightsholderName(boardId: string | undefined): void {
    if (!boardId) {
      return;
    }

    this.companyNameSubscription = this.companyService.getCompanybyBoardID(boardId).subscribe({
      next: (company: Company) => {
        const derivedName = (company as any)?.rightsholderName || company?.brandName || company?.legalName || '';
        if (derivedName) {
          this.currentRigtsholderName = derivedName;
        }
      },
      error: () => {
        // Keep existing value (if any) when lookup fails
      }
    });
  }

  ngAfterViewInit() {
    // Initialize with the default tab selected
    setTimeout(() => this.updateSliderPosition(this.activeTab), 0);
  }

  getRoles() {
    //this.spinnerService.show();
    this.userService.getAllRoles().subscribe((rolesResponse: any) => {


      // check logged in user if Superadmin
      let userRole = JSON.parse(localStorage.getItem('currentUser')!).user
        .roleName;

      if (userRole === UserRoleNames.Superadmin) {
        // If current logged in user board id doesnt match the board id being navigaated too
        // that means super admin wants to invite users inside this label 

        let currentNavigatedBoardId = this.route.snapshot.params['id'];

        if (currentNavigatedBoardId === this.boardId) {

          this.roles = rolesResponse.data.filter(
            (role: any) => role.name === UserRoleNames.Superadmin
          );

        } else {

          this.roles = rolesResponse.data.filter(
            (role: any) =>
              role.name !== UserRoleNames.Superadmin && role.name !== UserRoleNames.LabelAgent
          );
        }

      } else {
        this.roles = rolesResponse.data.filter(
          (role: any) =>
            role.name !== UserRoleNames.Superadmin && role.name !== UserRoleNames.LabelAgent
        );
      }

      // Sort roles by name in ascending order
      this.roles.sort((a, b) => a.name.localeCompare(b.name));

      //this.spinnerService.hide();
    });
  }

  showNotification(notification: Notification): void {
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
    }
    this.activeNotification = notification;
    this.notificationTimer = setTimeout(() => {
      this.activeNotification = null;
    }, 5000);
  }
  sendInvitation() {
    let _email = this.inviteForm.getRawValue().email;
    let _userRole: any = this.inviteForm.getRawValue().userRole;
    let _boardId = this.route.snapshot.params['id'];
    //this.spinnerService.show();
    this.userService
      .sendInvitation(_email!, _boardId, _userRole)
      .subscribe(
        (response) => {
          this.notificationService.showSuccess('An invitation has been sent to ' + _email);
          this.inviteForm.reset();



          //this.spinnerService.hide();
        },
        (err: any) => {
          this.notificationService.showError('Invitation failed to send. Please try again.');
          //this.spinnerService.hide();
        }
      );

    this.isInviteModalOpen = false;

    this.getLabelMembers();
  }

  getLabelMembers() {
    //this.spinnerService.show();
    this.userService.getLabelMembers(this.boardId).subscribe((response: any) => {
      this.members = response.data;
      //this.spinnerService.hide();
    });
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.updateSliderPosition(tab);

    // Update URL to reflect active tab
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge'
    });
  }

  updateSliderPosition(tab: string) {
    switch (tab) {
      case 'members':
        this.sliderStyle = { width: '196px', left: '8px' };
        break;
      case 'settings':
        this.sliderStyle = { width: '192px', left: '204px' };
        break;
      case 'superadmin':
        this.sliderStyle = { width: '192px', left: '396px' };
        break;
      default:
        this.sliderStyle = { width: '196px', left: '8px' };
    }
  }


  openInviteModal() {
    this.isInviteModalOpen = true;
  }

  closeInviteModal() {
    this.isInviteModalOpen = false;
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.paramsSubscription) {
      this.paramsSubscription.unsubscribe();
    }
    if (this.companyNameSubscription) {
      this.companyNameSubscription.unsubscribe();
    }
  }
}
